import { useState, useEffect, useCallback, useRef, Component } from 'react'
import { authRefresh, authLogout, onAuthChange, isOwnerAuth, getMyUserId as _getMyUserIdFromAuth, clearAuth } from './utils/auth'
import RegisterPage from './components/RegisterPage'

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(e) { return { error: e } }
  render() {
    if (this.state.error) return (
      <div style={{ padding: 24, fontFamily: 'monospace', fontSize: 13, color: '#d45a5a', background: '#05050d', minHeight: '100vh' }}>
        <div style={{ marginBottom: 8, fontWeight: 700 }}>React Error:</div>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{String(this.state.error)}</pre>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#8890aa', marginTop: 12 }}>{this.state.error?.stack}</pre>
      </div>
    )
    return this.props.children
  }
}

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint)
  useEffect(() => {
    let t
    const handler = () => { clearTimeout(t); t = setTimeout(() => setIsMobile(window.innerWidth < breakpoint), 80) }
    window.addEventListener('resize', handler)
    return () => { clearTimeout(t); window.removeEventListener('resize', handler) }
  }, [breakpoint])
  return isMobile
}
import { fetchHealth, saveProfile, getProfile, embedChunks, getChunksSample, getRepresentativeChunks, deepAnalysis, clearChunks, getChunksStats, extractFacts, extractFactsStart, getFacts, recalculate, getFactContradictions, getPublishedResumeBySlug, recordResumeVisit, saveOwnerPublicSettings, getSurveyStatus, getSurveyGroup1Questions, generateSurveyGroup2, submitSurvey, analyzeSurvey, getSurveyResponses, clearSurvey, getDevProgress, listParticipants, createInvite, setFactVerdict, getFactsExtractionStatus, extractFactsIncremental, generateSurveySession } from './utils/api'
import { getModelPref } from './utils/modelPrefs'
import ModelToggle from './components/ModelToggle'
import { getSettingsForUser } from './utils/chunkSettings'
import { analyzeAll } from './utils/analyze'
import BotLogs from './components/BotLogs'
import BotPrompt from './components/BotPrompt'
import Mindmap from './components/Mindmap'
import Development from './components/Development'
import Mentor from './components/Mentor'
import CareerNavigator from './components/CareerNavigator'
import VisitorChat from './components/VisitorChat'
import LoginModal from './components/LoginModal'
import NatalChart from './components/NatalChart'
import ThreadsEditor from './components/ThreadsEditor'
import SocionicsTab from './components/SocionicsTab'
import PhysiognomyTab from './components/PhysiognomyTab'
import MusicTab from './components/MusicTab'
import FactsViewer from './components/FactsViewer'
import VoiceNotesViewer from './components/VoiceNotesViewer'
import ParticipantsTab from './components/ParticipantsTab'
import ParticipantView from './components/ParticipantView'
import { FieldWithEvidence, ListWithEvidence } from './components/EvidencePanel'
import ProfileResult, { C, font, GlassPanel, RadarChart } from './components/ProfileResult'
import {
  filterMyMessages,
  buildContextualChunks,
  analyzeStyle,
} from './utils/filters'
import FileUpload from './components/FileUpload'
import Survey, { SurveyAnalyzingScreen, SurveyDoneScreen } from './components/Survey'
import SurveyChoice from './components/SurveyChoice'
import { SurveyEvidencePanel } from './components/EvidencePanel'
import ApplicationPackage from './components/ApplicationPackage'
import { VisibilitySettings } from './components/PublicParticipantPage'
import { chunkResumeText } from './utils/parsers'

// ── SPA routing ───────────────────────────────────────────────────────────────
const _pathname = window.location.pathname
const _resumeSlugMatch = _pathname.match(/^\/r\/([^/]+)$/)
const _participantPublicMatch = _pathname.match(/^\/u\/(\d+)$/)
const _inviteMatch = _pathname.match(/^\/invite\/([^/]+)$/)

// C, font, GlassPanel, RadarChart импортированы из ProfileResult.jsx

const STEPS = [
  { key: 'parsing',    label: 'Парсинг данных...' },
  { key: 'filtering',  label: 'Фильтрация сообщений...' },
  { key: 'chunking',   label: 'Построение контекста...' },
  { key: 'style',      label: 'Анализ стилистики...' },
  { key: 'embedding',  label: 'Сохранение в базу знаний...' },
  { key: 'analysis',   label: 'AI-анализ личности и карьеры...' },
  { key: 'saving',     label: 'Сохранение профиля...' },
]

const _ownerParticipantId = localStorage.getItem('dt_owner_participant_id')
const MY_USER_ID = _ownerParticipantId ? `participant_${_ownerParticipantId}` : 'user'

const DEFAULT_OWNER_PUBLIC_SETTINGS = {
  show_personality: true,
  show_competencies: true,
  show_career: true,
  show_motivation: true,
  show_mindmap: true,
  show_natal: true,
  show_chat: true,
  show_facts: true,
  show_music: true,
}

// Определяет, является ли объект evidence-объектом от Claude
function isEvidenceObj(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const keys = Object.keys(v)
  return keys.includes('confidence') && (keys.includes('pattern') || keys.includes('quotes'))
}

// Конвертирует evidence-объект в строку
function evidenceToStr(v) {
  if (typeof v === 'string') return v
  if (v == null) return ''
  return v.pattern || v.value || v.name || v.text || v.strength || ''
}

// Рекурсивно проходит по ЛЮБОМУ разделу профиля (personality, career, etc.)
// и заменяет evidence-объекты строками там где они не ожидаются
// ИСКЛЮЧАЕТ evidence_data/_evidence — там объекты нужны
function sanitizeSection(obj, depth = 0) {
  if (depth > 8 || !obj || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) {
    return obj.map(item => {
      if (isEvidenceObj(item)) return evidenceToStr(item)
      if (typeof item === 'object') return sanitizeSection(item, depth + 1)
      return item
    })
  }
  const result = { ...obj }
  for (const key of Object.keys(result)) {
    const val = result[key]
    if (isEvidenceObj(val)) {
      result[key] = evidenceToStr(val)
    } else if (Array.isArray(val)) {
      result[key] = sanitizeSection(val, depth + 1)
    } else if (val && typeof val === 'object') {
      result[key] = sanitizeSection(val, depth + 1)
    }
  }
  return result
}

function sanitizeProfile(raw) {
  if (!raw) return raw
  // Санитизируем все разделы КРОМЕ evidence_data/_evidence (там объекты нужны)
  const sections = ['personality','personality_data','competencies','competencies_data',
                    'career','career_data','motivation','motivation_data','style_analysis']
  for (const key of sections) {
    if (raw[key]) raw[key] = sanitizeSection(raw[key])
  }
  return raw
}

// Строим контекст из репрезентативных чанков из DB (используется в основном потоке анализа)
function buildContextFromDBChunks(chunks, sourceWeights = {}) {
  const w = {
    diary: sourceWeights.diary ?? 1,
    personal: sourceWeights.personal ?? 1,
    group: sourceWeights.group ?? 1,
    threads: sourceWeights.threads ?? 1,
    resume: sourceWeights.resume ?? 1,
  }
  const byType = { journal: [], resume: [], threads: [], personal: [], group: [] }
  for (const c of chunks) {
    const t = c.chatType || 'group'
    const weight = t === 'journal' ? w.diary : t === 'personal' ? w.personal :
      t === 'threads' ? w.threads : t === 'resume' ? w.resume : w.group
    if (weight <= 0) continue
    if (byType[t]) byType[t].push(c.text)
    else byType.group.push(c.text)
  }
  const parts = []
  if (byType.journal.length)
    parts.push('## Личный дневник (первичный источник)\n' + byType.journal.join('\n\n---\n\n'))
  if (byType.resume.length)
    parts.push('## Резюме\n' + byType.resume.join('\n\n---\n\n'))
  if (byType.threads.length)
    parts.push('## Threads-посты (публичные высказывания)\n' + byType.threads.join('\n\n---\n\n'))
  if (byType.personal.length)
    parts.push('## Личные переписки 1:1\n' + byType.personal.join('\n\n---\n\n'))
  if (byType.group.length)
    parts.push('## Групповые чаты (мат = манера общения, не черта характера)\n' + byType.group.join('\n\n---\n\n'))
  return parts.join('\n\n====\n\n')
}

// Собираем контекст для Claude — выбираем репрезентативные срезы, не весь объём
function buildContext({ telegram, journal, resume, threads }, sourceWeights = {}) {
  const parts = []
  const w = {
    diary: sourceWeights.diary ?? 1,
    personal: sourceWeights.personal ?? 1,
    group: sourceWeights.group ?? 1,
    threads: sourceWeights.threads ?? 1,
    resume: sourceWeights.resume ?? 1,
  }

  if (telegram?.chunks?.length) {
    const pool = telegram.chunks

    // Разделяем личные и групповые чанки
    const personalChunks = pool.filter((c) => c.chatType === 'personal')
    const groupChunks = pool.filter((c) => c.chatType !== 'personal')

    // Вес определяет сколько чанков берём из каждого типа (1 = 1 чанк, 2 = 2 чанка, итд)
    const pickFrom = (arr, weight) => {
      if (!arr.length) return []
      const count = Math.max(1, Math.round(weight))
      if (arr.length <= count) return arr
      const step = Math.floor(arr.length / count)
      return Array.from({ length: count }, (_, i) => arr[Math.min(i * step, arr.length - 1)])
    }

    const personalPicks = pickFrom(personalChunks, w.personal)
    const groupPicks = pickFrom(groupChunks, w.group)

    if (personalPicks.length) {
      const text = personalPicks.map((c) => c.text).join('\n\n---\n\n')
      parts.push(`## Личные переписки 1:1 (отражают глубинный стиль общения)\n${text}`)
    }
    if (groupPicks.length) {
      const text = groupPicks.map((c) => c.text).join('\n\n---\n\n')
      parts.push(`## Групповые чаты (разговорный регистр, мат = манера общения между друзьями)\n${text}`)
    }
  }

  if (journal?.length) {
    const count = Math.max(1, Math.round(w.diary * 3))
    const sample = journal
      .slice(0, count)
      .map((e) => `[${e.date}] ${e.text.slice(0, 400)}`)
      .join('\n\n')
    parts.push(`## Личный дневник (первичный источник для анализа личности)\n${sample}`)
  }

  if (resume) {
    parts.push(`## Резюме (профессиональный контекст)\n${resume.slice(0, 1500)}`)
  }

  if (threads) {
    const count = Math.max(3, Math.round(w.threads * 12))
    const postsSample = (threads.posts || [])
      .slice(0, count)
      .map((p) => p.text)
      .join('\n')
    if (postsSample) parts.push(`## Публичные посты Threads (публичная самопрезентация)\n${postsSample}`)

    if (threads.threadsMyName && threads.dm?.length) {
      const myDMs = threads.dm.filter(m => (m.from || '') === threads.threadsMyName)
      const dmCount = Math.max(5, Math.round(w.personal * 10))
      const dmSample = myDMs.slice(0, dmCount).map(m => m.text).join('\n')
      if (dmSample) parts.push(`## Личные переписки Threads DM (1:1)\n${dmSample}`)
    }
  }

  return parts.join('\n\n====\n\n')
}

// ---------- Компонент прогресса ----------

function ProgressBar({ step }) {
  const idx = STEPS.findIndex((s) => s.key === step)
  const basePct = idx < 0 ? 0 : Math.round((idx / STEPS.length) * 100)
  const stepPct  = idx < 0 ? 0 : Math.round(((idx + 1) / STEPS.length) * 100)
  const label = STEPS[idx]?.label || ''
  const isAnalysis = step === 'analysis'

  const [elapsed, setElapsed] = useState(0)
  const [dots, setDots] = useState('.')
  const startRef = useRef(null)

  useEffect(() => {
    if (!step) { setElapsed(0); return }
    startRef.current = Date.now()
    setElapsed(0)
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000))
      setDots(d => d.length >= 3 ? '.' : d + '.')
    }, 1000)
    return () => clearInterval(iv)
  }, [step])

  // Во время анализа бар медленно ползёт к 99% за ~120с, потом пульсирует
  const EXPECTED = 90
  const displayPct = isAnalysis
    ? Math.round(basePct + (99 - basePct) * Math.min(elapsed / EXPECTED, 1))
    : stepPct

  const elapsedStr = elapsed >= 60
    ? `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`
    : `${elapsed}с`

  const isLong = isAnalysis && elapsed > EXPECTED

  return (
    <div style={{ marginBottom: 24 }}>
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes pulse-bar {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{
        display: 'flex', justifyContent: 'space-between', marginBottom: 6,
        fontFamily: font.body, fontSize: 13, color: C.textMuted,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {isAnalysis ? (
            <>
              <span style={{
                display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                background: C.accent,
                animation: 'pulse-bar 1.2s ease-in-out infinite',
              }} />
              {label.replace('...', '')}
              <span style={{ color: C.accent, fontFamily: font.mono, fontSize: 11 }}>
                {elapsedStr}
              </span>
            </>
          ) : (
            <>{label.replace('...', '')}<span style={{ opacity: 0.5 }}>{dots}</span></>
          )}
        </span>
        <span style={{ fontFamily: font.mono, fontSize: 11 }}>
          {isLong ? '⟳' : `${displayPct}%`}
        </span>
      </div>
      <div style={{ height: 3, background: C.border, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          height: '100%',
          width: `${displayPct}%`,
          background: `linear-gradient(90deg, ${C.accent}88, ${C.accent})`,
          borderRadius: 2,
          transition: isAnalysis ? 'width 1s linear' : 'width 0.35s ease',
        }} />
        {isAnalysis && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '20%', height: '100%',
            background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)`,
            animation: 'shimmer 2s ease-in-out infinite',
          }} />
        )}
      </div>
      {isAnalysis && (
        <div style={{
          marginTop: 6, fontSize: 11, color: C.textMuted, fontFamily: font.body,
          opacity: 0.7,
        }}>
          {elapsed < 20 && 'Отправляем данные Claude...'}
          {elapsed >= 20 && elapsed < 50 && 'Claude читает ваши сообщения и строит профиль...'}
          {elapsed >= 50 && elapsed < 90 && 'Формируем доказательную базу и карьерные рекомендации...'}
          {elapsed >= 90 && 'Почти готово — Claude заканчивает финальную сборку...'}
        </div>
      )}
    </div>
  )
}

// ---------- Статистика данных ----------

function DataStatsBar({ stats }) {
  if (!stats || stats.total === 0) return null
  const t = stats.by_type || {}
  const tgGroup = t.group || 0
  const tgPersonal = t.personal || 0
  const journal = t.journal || 0
  const resume = t.resume || 0
  const threads = (t.threads || 0) + (t.threads_post || 0)

  const items = []
  if (tgGroup > 0)    items.push({ label: `групповые чаты: ~${(tgGroup * 40).toLocaleString('ru')} сообщ.`, chunks: tgGroup })
  if (tgPersonal > 0) items.push({ label: `личные чаты: ~${(tgPersonal * 40).toLocaleString('ru')} сообщ.`, chunks: tgPersonal })
  if (journal > 0)    items.push({ label: `дневник: ${journal} зап.`, chunks: journal })
  if (threads > 0)    items.push({ label: `Threads: ${threads} постов`, chunks: threads })
  if (resume > 0)     items.push({ label: 'резюме: ✓', chunks: resume })

  if (items.length === 0) return null

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '6px 12px', alignItems: 'center',
      marginBottom: 14, padding: '7px 12px',
      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(180,185,230,0.07)',
      borderRadius: 8, fontFamily: 'Space Mono, monospace', fontSize: 11,
    }}>
      <span style={{ color: 'rgba(180,185,212,0.4)', marginRight: 2 }}>данных:</span>
      {items.map((item, i) => (
        <span key={i} style={{ color: 'rgba(180,185,212,0.65)' }}>
          {item.label}
          <span style={{ color: 'rgba(180,185,212,0.3)', fontSize: 10 }}> · {item.chunks} чанк{item.chunks === 1 ? '' : item.chunks < 5 ? 'а' : 'ов'}</span>
        </span>
      ))}
      <span style={{ color: 'rgba(180,185,212,0.25)', marginLeft: 'auto' }}>{stats.total} всего</span>
    </div>
  )
}

// ─── OwnerControlsPanel: impersonate + invite (JSON-tab only) ────────────────

function OwnerControlsPanel({ onImpersonate, participants }) {
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteUrl, setInviteUrl]         = useState(null)
  const [inviteCopied, setInviteCopied]   = useState(false)

  async function handleCreateInvite() {
    setInviteLoading(true)
    setInviteUrl(null)
    try {
      const data = await createInvite('Приглашение от владельца', 14)
      if (data.invite_url) setInviteUrl(data.invite_url)
    } catch (e) {
      console.error('invite error', e)
    } finally {
      setInviteLoading(false)
    }
  }

  function copyInvite() {
    if (!inviteUrl) return
    navigator.clipboard.writeText(inviteUrl).catch(() => {})
    setInviteCopied(true)
    setTimeout(() => setInviteCopied(false), 2000)
  }

  const panelStyle = {
    marginTop: 20, padding: '16px 20px',
    background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(180,185,230,0.1)',
    borderRadius: 12,
    fontFamily: 'Inter, sans-serif',
  }
  const labelStyle = { fontSize: 11, color: '#8890aa', marginBottom: 8, letterSpacing: '0.08em', textTransform: 'uppercase' }
  const btnStyle = (active) => ({
    padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', fontSize: 12,
    background: active ? 'rgba(180,185,230,0.15)' : 'transparent',
    border: `1px solid ${active ? 'rgba(180,185,230,0.4)' : 'rgba(180,185,230,0.12)'}`,
    color: active ? '#dce0f0' : '#8890aa',
  })

  return (
    <div style={panelStyle}>
      {/* Impersonation — полное переключение в UI участника */}
      <div style={{ marginBottom: 20 }}>
        <div style={labelStyle}>Просматривать как</div>
        <p style={{ margin: '0 0 10px', fontSize: 11, color: '#666', lineHeight: 1.4 }}>
          Переключиться в полный UI участника — видишь то же что и он, можешь вносить изменения от его имени.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {participants.length === 0 && (
            <span style={{ fontSize: 12, color: '#666' }}>Нет участников</span>
          )}
          {participants.map(p => (
            <button
              key={p.id}
              style={btnStyle(false)}
              onClick={() => onImpersonate(p.id, p.display_name)}
            >
              {p.display_name} →
            </button>
          ))}
        </div>
      </div>

      {/* Invite creation (generic) */}
      <div>
        <div style={labelStyle}>Пригласить нового участника</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleCreateInvite}
            disabled={inviteLoading}
            style={{
              padding: '6px 14px', borderRadius: 6, cursor: inviteLoading ? 'default' : 'pointer',
              fontFamily: 'Inter, sans-serif', fontSize: 12,
              background: 'rgba(180,185,230,0.08)', border: '1px solid rgba(180,185,230,0.2)',
              color: '#b8bdd4', opacity: inviteLoading ? 0.5 : 1,
            }}
          >
            {inviteLoading ? '⏳ Создаю…' : '+ Создать ссылку-приглашение'}
          </button>
          {inviteUrl && (
            <>
              <input
                readOnly value={inviteUrl}
                onClick={e => e.target.select()}
                style={{
                  flex: 1, minWidth: 200, padding: '5px 10px', borderRadius: 6,
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(180,185,230,0.15)',
                  color: '#b8bdd4', fontFamily: 'Space Mono, monospace', fontSize: 11, outline: 'none',
                }}
              />
              <button
                onClick={copyInvite}
                style={{
                  padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif', fontSize: 12,
                  background: inviteCopied ? 'rgba(77,200,138,0.12)' : 'rgba(180,185,230,0.08)',
                  border: `1px solid ${inviteCopied ? 'rgba(77,200,138,0.3)' : 'rgba(180,185,230,0.2)'}`,
                  color: inviteCopied ? '#4dc88a' : '#b8bdd4',
                }}
              >
                {inviteCopied ? '✓ скопировано' : 'копировать'}
              </button>
            </>
          )}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 11, color: '#666' }}>
          Ссылка одноразовая, 14 дней. Или создай инвайт на карточке участника в разделе «Свои» — для привязки к существующему профилю.
        </p>
      </div>
    </div>
  )
}

// ---------- Главный App ----------

export default function App() {
  const [apiStatus, setApiStatus] = useState('checking')
  const [profile, setProfile] = useState(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [deepLoading, setDeepLoading] = useState(false)
  const [recalcLoading, setRecalcLoading] = useState(false)
  const [recalcStages, setRecalcStages] = useState(null)
  const [factsLoading, setFactsLoading] = useState(false)
  const [factsCount, setFactsCount] = useState(null)
  const [factsSubTab, setFactsSubTab] = useState('facts')
  const [showFacts, setShowFacts] = useState(false)
  const [extractionRunning, setExtractionRunning] = useState(false) // global indicator
  const [extractionJobId, setExtractionJobId] = useState(null)
  const [surveySessionsCompleted, setSurveySessionsCompleted] = useState(0)
  const [surveySessionLoading, setSurveySessionLoading] = useState(false)
  const [showSurveyAnalyzeBtn, setShowSurveyAnalyzeBtn] = useState(false)
  const [step, setStep] = useState(null)
  const [error, setError] = useState(null)
  const [msg, setMsg] = useState(null)
  const [showUpload, setShowUpload] = useState(false)
  const [clearConfirm, setClearConfirm] = useState(false)
  const [pendingStyleData, setPendingStyleData] = useState(null) // ingest done this session, ready to analyze
  // Survey
  const [learningBonuses, setLearningBonuses] = useState({})
  const [surveyStatus,    setSurveyStatus]    = useState(null)
  const [surveyStep,      setSurveyStep]      = useState(null)   // null|'choice'|'group1'|'group2'|'analyzing'
  const [surveyQuestions, setSurveyQuestions] = useState([])
  const [surveyGroupNum,  setSurveyGroupNum]  = useState(1)
  const [surveyLoading,   setSurveyLoading]   = useState(false)
  const [surveyResponses, setSurveyResponses] = useState([])
  const [dataStats, setDataStats] = useState(null)
  const [view, setView] = useState(_resumeSlugMatch ? 'resume_apply' : 'analysis')
  const [resumeData, setResumeData] = useState(null)
  const [careerRolesState, setCareerRolesState] = useState(null) // persists across tab switches
  const scrollRef = useRef(null)
  const [isOwner, setIsOwner] = useState(() => localStorage.getItem('dt_owner') === 'true')
  const [impersonating, setImpersonating] = useState(null) // { id, displayName } — owner views as participant
  const [participantsList, setParticipantsList] = useState([])  // for impersonation dropdown
  const [ownerPublicSettings, setOwnerPublicSettings] = useState(() => {
    // Init from cache immediately — prevents flash of hidden tabs before profile loads
    try {
      const cached = localStorage.getItem(`dt_profile_cache_${MY_USER_ID}`)
      if (cached) {
        const meta = JSON.parse(cached)?.metadata || {}
        if (meta.owner_public_settings) return meta.owner_public_settings
      }
    } catch {}
    return null
  })
  const [ownerVisibilitySaving, setOwnerVisibilitySaving] = useState(false)
  const [participantSession, setParticipantSession] = useState(() => {
    const id = localStorage.getItem('dt_participant_id')
    const name = localStorage.getItem('dt_participant_name')
    return id ? { participant_id: parseInt(id), display_name: name } : null
  })
  const [showLogin, setShowLogin] = useState(false)
  const [analysisTab, setAnalysisTab] = useState('personality')
  const [lastTelegramData, setLastTelegramData] = useState(null)
  const [lastThreadsData, setLastThreadsData] = useState(null)
  const [sourceWeights, setSourceWeights] = useState(() => {
    try {
      const saved = localStorage.getItem('dt_source_weights')
      return saved ? JSON.parse(saved) : { diary: 1, personal: 1, group: 1, threads: 1, resume: 1 }
    } catch { return { diary: 1, personal: 1, group: 1, threads: 1, resume: 1 } }
  })
  const isMobile = useIsMobile()

  // ── JWT: silent session restore on page load ──────────────────────────────
  useEffect(() => {
    authRefresh().then(data => {
      if (data) {
        setIsOwner(data.role === 'owner' || data.is_owner)
        if (!data.is_owner && data.role !== 'owner' && data.participant_id) {
          setParticipantSession({ participant_id: data.participant_id, display_name: data.display_name })
        }
      }
    })
    // React to auth state changes (login / logout)
    return onAuthChange(state => {
      setIsOwner(state.role === 'owner')
    })
  }, [])

  // Load participants list for actAs dropdown (owner only)
  useEffect(() => {
    if (!isOwner) return
    listParticipants(MY_USER_ID).then(data => {
      if (Array.isArray(data)) setParticipantsList(data.filter(p => !p.is_owner))
    }).catch(() => {})
  }, [isOwner])

  function handleImpersonate(id, displayName) {
    setImpersonating({ id, displayName })
  }

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [view])

  useEffect(() => {
    let profileDone = false
    let retryTimer = null

    const CACHE_KEY = `dt_profile_cache_${MY_USER_ID}`

    function applyProfileData(data) {
      const raw = {
        personality: data.personality_data,
        competencies: data.competencies_data,
        career: data.career_data,
        motivation: data.motivation_data,
        personality_data: data.personality_data,
        competencies_data: data.competencies_data,
        career_data: data.career_data,
        motivation_data: data.motivation_data,
        style_analysis: data.metadata?.style_analysis,
        _evidence: data.evidence_data || {},
        evidence_data: data.evidence_data || {},
        _botPrompt: data.metadata?.bot_prompt,
        _deepAnalysis: data.metadata?.deep_analysis || false,
        _deepChunks: data.metadata?.deep_analysis_chunks,
      }
      sanitizeProfile(raw)
      setShowUpload(false)
      setProfile(raw)
      const meta = data.metadata || {}
      if (meta.owner_public_settings) setOwnerPublicSettings(meta.owner_public_settings)
    }

    // Show cached profile immediately — user sees their data even on slow/broken connections
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached) {
      try { applyProfileData(JSON.parse(cached)) } catch { localStorage.removeItem(CACHE_KEY) }
    }

    function doLoadProfile() {
      getProfile(MY_USER_ID)
        .then((data) => {
          if (data && !data.error) {
            localStorage.setItem(CACHE_KEY, JSON.stringify(data))
            applyProfileData(data)
            profileDone = true
          } else {
            // Genuine 404 — profile deleted or never existed
            localStorage.removeItem(CACHE_KEY)
            // Show SurveyChoice so user can pick between questionnaire and file upload
            setSurveyStep('choice')
            profileDone = true
          }
        })
        .catch(() => {
          // Network error — if we have cache it's already showing; only show upload if no cache
          if (!cached) setShowUpload(true)
          // profileDone stays false → checkHealth retry will call doLoadProfile again
        })
        .finally(() => setProfileLoading(false))
    }

    function checkHealth() {
      fetchHealth()
        .then(() => {
          setApiStatus('online')
          if (!profileDone) doLoadProfile()
        })
        .catch(() => {
          setApiStatus('offline')
          retryTimer = setTimeout(checkHealth, 8000)
        })
    }

    checkHealth()
    doLoadProfile()

    getChunksStats(MY_USER_ID).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
    if (isOwner) getFacts(MY_USER_ID).then(r => { if (r.total > 0) setFactsCount(r.total) }).catch(() => {})
    getDevProgress(MY_USER_ID).then(r => { if (r.bonuses) setLearningBonuses(r.bonuses) }).catch(() => {})
    getSurveyStatus(MY_USER_ID).then(status => {
      setSurveyStatus(status)
      setSurveySessionsCompleted(status.group2_sessions_completed || 0)
      if (status.group1_done || status.group2_done) {
        getSurveyResponses(MY_USER_ID).then(d => setSurveyResponses(d.responses || [])).catch(() => {})
      }
    }).catch(() => {})
    // Check if extraction is already running (e.g. after page refresh)
    getFactsExtractionStatus(MY_USER_ID).then(s => { if (s.running) startPollingExtraction() }).catch(() => {})

    if (_resumeSlugMatch) {
      const slug = _resumeSlugMatch[1]
      getPublishedResumeBySlug(slug)
        .then(r => { if (r && !r.error) setResumeData(r) })
        .catch(() => {})
      recordResumeVisit(slug)
    }

    return () => { if (retryTimer) clearTimeout(retryTimer) }
  }, [])

  // Загрузка данных: парсит, чанкует, встраивает в БД. Анализ НЕ запускает —
  // это отдельный явный шаг (runAnalyzeNow), см. task 2 сессии "участники + пульсация".
  async function runIngest(sources) {
    if (sources.telegram) setLastTelegramData(sources.telegram)
    if (sources.threads) setLastThreadsData(sources.threads)
    setLoading(true)
    setError(null)
    setMsg(null)
    setShowUpload(false)

    try {
      // Шаг 1: Парсинг уже выполнен в FileUpload, здесь обрабатываем
      setStep('parsing')
      await tick()

      // Шаг 2: Фильтрация Telegram
      setStep('filtering')
      await tick()

      let myMessages = []

      if (sources.telegram) {
        const { messages, myName } = sources.telegram
        myMessages = filterMyMessages(messages, myName)
      }

      // Шаг 3: Чанки
      setStep('chunking')
      await tick()

      const chunks = myMessages.length ? buildContextualChunks(myMessages) : []

      // Шаг 4: Стилистика (сохраняется только в памяти до момента запуска анализа)
      setStep('style')
      await tick()
      const styleData = analyzeStyle(myMessages.length ? myMessages : [])

      // Шаг 5: Встраиваем чанки в БД. Ждём завершения — следом больше не запускается анализ,
      // так что торопиться fire-and-forget'ом (как раньше, из-за Cloudflare 100с) не нужно.
      setStep('embedding')
      await tick()
      const extraChunks = []
      if (sources.journal?.length) {
        sources.journal.forEach((j, i) => {
          if (j.text) extraChunks.push({ text: j.text.slice(0, 2000), source: `journal_${i}`, chatType: 'journal', dateRange: j.date || '' })
        })
      }
      if (sources.resume) {
        chunkResumeText(sources.resume).forEach(t => extraChunks.push({ text: t, source: 'resume', chatType: 'resume', dateRange: '' }))
      }
      if (sources.threads?.posts?.length) {
        sources.threads.posts.slice(0, 30).forEach((t, i) => {
          if (t.text) extraChunks.push({ text: t.text.slice(0, 500), source: `threads_post_${i}`, chatType: 'threads', dateRange: t.date || '' })
        })
      }
      if (sources.threads?.threadsMyName && sources.threads?.dm?.length) {
        const myDMs = sources.threads.dm.filter(m => m.from === sources.threads.threadsMyName)
        myDMs.slice(0, 100).forEach((m, i) => {
          if (m.text) extraChunks.push({ text: m.text.slice(0, 500), source: `threads_dm_${i}`, chatType: 'personal', dateRange: m.date || '' })
        })
      }
      if (chunks.length > 0) await embedChunks(MY_USER_ID, chunks).catch(e => console.warn('[App] embedChunks:', e))
      if (extraChunks.length > 0) await embedChunks(MY_USER_ID, extraChunks).catch(e => console.warn('[App] extraChunks:', e))
      // Backend автоматически запустит инкрементальное извлечение фактов после embed
      startPollingExtraction()

      setPendingStyleData(styleData)
      getChunksStats(MY_USER_ID).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
      setMsg('Данные загружены. Можно проверить факты в разделе «Факты» или сразу запустить анализ.')
    } catch (e) {
      console.error('[App] runIngest error:', e)
      setError(`Ошибка загрузки данных: ${e.message}`)
      if (!profile) setShowUpload(true)
    } finally {
      setLoading(false)
      setStep(null)
    }
  }

  // Запускает сам анализ поверх уже загруженных в БД данных. Требует, чтобы runIngest
  // отработал в этой же сессии (styleData считается на клиенте и не персистится) —
  // если сессия потеряна (перезагрузка страницы), просит загрузить файл заново.
  async function runAnalyzeNow() {
    if (!pendingStyleData) {
      setError('Сессия загрузки истекла — загрузите файл ещё раз, чтобы запустить анализ.')
      setShowUpload(true)
      return
    }
    setCareerRolesState(null)
    try { localStorage.removeItem('career_roles_cache') } catch {}
    setLoading(true)
    setError(null)
    setMsg(null)

    try {
      setStep('analysis')
      await tick()
      const userSettings = getSettingsForUser(MY_USER_ID)
      let mainContext = ''
      let dbTotal = 0
      try {
        const repResult = await getRepresentativeChunks(MY_USER_ID, userSettings.n || 40, userSettings.quotas || null)
        dbTotal = repResult?.total || 0
        if (repResult?.chunks?.length) {
          mainContext = buildContextFromDBChunks(repResult.chunks, sourceWeights)
        }
      } catch (e) { console.warn('[App] representative chunks failed:', e) }

      if (!mainContext.trim()) {
        throw new Error(`Нет данных для анализа в базе (чанков: ${dbTotal}).`)
      }

      const analysisResult = await analyzeAll(mainContext, { user_id: MY_USER_ID, fast_mode: userSettings.fast_mode || false })
      const { personality, competencies, career, motivation, evidence, usage } = analysisResult

      // Шаг 7: Сохранение
      setStep('saving')
      const profileData = {
        personality,
        competencies,
        career,
        motivation,
        evidence_data: evidence || null,
        style_analysis: pendingStyleData,
        tokens_used: (usage?.input_tokens || 0) + (usage?.output_tokens || 0),
        cost_usd: usage?.cost_usd || 0,
      }
      const saveResult = await saveProfile(MY_USER_ID, profileData)
      const runCount = saveResult?.run_count || 1

      const freshProfile = { ...profileData, _usage: usage, _evidence: evidence || {} }
      sanitizeProfile(freshProfile)
      setProfile(freshProfile)
      try { localStorage.setItem(`dt_profile_cache_${MY_USER_ID}`, JSON.stringify(saveResult || profileData)) } catch {}
      getChunksStats(MY_USER_ID).then(s => { if (!s.error) setDataStats(s) }).catch(() => {})
      setPendingStyleData(null)
      setShowUpload(false)
      const costStr = usage?.cost_usd ? ` · $${usage.cost_usd.toFixed(4)}` : ''
      setMsg(`Профиль обновлён (запуск №${runCount})${costStr}`)
    } catch (e) {
      console.error('[App] runAnalyzeNow error:', e)
      setError(`Ошибка анализа: ${e.message}`)
    } finally {
      setLoading(false)
      setStep(null)
    }
  }

  function tick() {
    return new Promise((r) => setTimeout(r, 50))
  }

  async function runDeepAnalysis(userId = MY_USER_ID) {
    setDeepLoading(true)
    setError(null)
    setMsg(null)
    try {
      const result = await deepAnalysis(userId)
      if (result.error) throw new Error(result.error)
      const { personality, competencies, career, motivation, evidence, usage, chunks_analyzed, batches } = result
      if (userId === MY_USER_ID) {
        setProfile(prev => ({
          ...prev,
          personality, competencies, career, motivation,
          _evidence: evidence || {},
          evidence_data: evidence || {},
          _deepAnalysis: true,
          _deepChunks: chunks_analyzed,
        }))
        const costStr = usage?.cost_usd ? ` · $${usage.cost_usd.toFixed(4)}` : ''
        setMsg(`Глубокий анализ завершён: ${chunks_analyzed} чанков, ${batches} батчей${costStr}`)
      }
      return result
    } catch (e) {
      console.error('[App] runDeepAnalysis error:', e)
      setError(`Ошибка глубокого анализа: ${e.message}`)
    } finally {
      setDeepLoading(false)
    }
  }

  // runExtractKnowledge (/api/extract snapshot path) retired — see
  // server/archive/legacy_haiku_fact_extraction.py. Use runExtractFacts.

  async function runExtractFacts(userId = MY_USER_ID) {
    setFactsLoading(true)
    setError(null)
    setMsg(null)
    try {
      const jobId = await extractFactsStart(userId)
      setExtractionJobId(jobId)
      setShowFacts(true)
      if (view !== 'facts') setView('facts')
    } catch (e) {
      setError(`Ошибка извлечения фактов: ${e.message}`)
    } finally {
      setFactsLoading(false)
    }
  }

  // Poll extraction-status and show global indicator
  async function startPollingExtraction() {
    try {
      const status = await getFactsExtractionStatus(MY_USER_ID)
      if (status.running && status.job_id) {
        setExtractionRunning(true)
        setExtractionJobId(status.job_id)
        // Poll until done
        const deadline = Date.now() + 90 * 60 * 1000
        while (Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 6000))
          try {
            const job = await getFactsExtractionStatus(MY_USER_ID)
            if (!job.running) {
              setExtractionRunning(false)
              setExtractionJobId(null)
              // Refresh facts count
              getFacts(MY_USER_ID).then(r => { if (r.total !== undefined) setFactsCount(r.total) }).catch(() => {})
              break
            }
          } catch { /* continue */ }
        }
        setExtractionRunning(false)
      }
    } catch { /* silent */ }
  }

  async function runRecalculate(userId = MY_USER_ID) {
    try {
      const { contradictions } = await getFactContradictions(userId)
      const pending = contradictions?.length || 0
      if (pending > 0 && !confirm(`${pending} неразрешённых противоречий в фактах. Пересчитать всё равно?`)) return
    } catch { /* soft warning only — proceed if the check itself fails */ }

    setRecalcLoading(true)
    setRecalcStages(null)
    setError(null)
    try {
      const personalityModel = getModelPref('personality')
      const socionicsModel = getModelPref('socionics')
      const { job_id } = await recalculate(userId, personalityModel, socionicsModel)
      let attempts = 0
      while (attempts < 165) {
        await new Promise(r => setTimeout(r, 4000))
        attempts++
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/analyze/job/${job_id}`)
        if (!res.ok) { await new Promise(r => setTimeout(r, 4000)); continue }
        const job = await res.json()
        if (job.stages) setRecalcStages(job.stages)
        if (job.status === 'done') {
          const result = job.result
          const { personality, competencies, career, motivation, evidence, usage } = result
          const freshProfile = { personality, competencies, career, motivation, evidence_data: evidence || null, _usage: usage, _evidence: evidence || {} }
          sanitizeProfile(freshProfile)
          setProfile(freshProfile)
          const costStr = usage?.cost_usd ? ` · $${usage.cost_usd.toFixed(4)}` : ''
          setMsg(`Личность и соционика пересчитаны${costStr}`)
          return
        }
        if (job.status === 'error') throw new Error(job.error || 'Ошибка пересчёта')
        if (job.status === 'not_found') throw new Error('Задача потеряна (сервер перезапустился)')
      }
      throw new Error('Таймаут пересчёта')
    } catch (e) {
      setError(`Ошибка пересчёта: ${e.message}`)
    } finally {
      setRecalcLoading(false)
      setRecalcStages(null)
    }
  }

  // ── Survey ──────────────────────────────────────────────────────────────────
  async function startSurvey() {
    setSurveyLoading(true)
    try {
      const data = await getSurveyGroup1Questions()
      setSurveyQuestions(data.questions || [])
      setSurveyGroupNum(1)
      setSurveyStep('group1')
    } catch (e) {
      setError('Ошибка загрузки вопросов: ' + e.message)
    } finally {
      setSurveyLoading(false)
    }
  }

  async function handleSurveySubmit(responses) {
    setSurveyLoading(true)
    try {
      // surveyGroupNum: 1 = group1, 2 = group2 (first), 201..205 = extra sessions
      const isExtraSession = surveyGroupNum >= 200
      const sessionNum = isExtraSession ? surveyGroupNum - 200 : 1
      const groupNum = isExtraSession ? 2 : surveyGroupNum

      await submitSurvey(MY_USER_ID, groupNum, responses, sessionNum)

      if (surveyGroupNum === 1) {
        const result = await generateSurveyGroup2(MY_USER_ID)
        if (result.error) throw new Error(result.error)
        setSurveyQuestions(result.questions || [])
        setSurveyGroupNum(2)
        setSurveyStep('group2')
      } else if (isExtraSession) {
        // Extra session done — show analyze button
        const newCount = sessionNum
        setSurveySessionsCompleted(newCount)
        setSurveyStep(null)
        setSurveyStatus(s => ({ ...s, group2_done: true }))
        setShowSurveyAnalyzeBtn(true)
        setAnalysisTab('survey')
        setMsg(`Сессия ${sessionNum}/5 завершена. Нажмите «Обновить анализ» для пересчёта профиля.`)
      } else {
        setSurveyStep('analyzing')
        await runSurveyAnalysis()
      }
    } catch (e) {
      setError('Ошибка анкеты: ' + e.message)
      setSurveyStep(surveyGroupNum === 1 ? 'group1' : 'group2')
    } finally {
      setSurveyLoading(false)
    }
  }

  async function runSurveyAnalysis() {
    setError(null)
    try {
      const result = await analyzeSurvey(MY_USER_ID)
      if (result?.error) throw new Error(result.error)
      if (result?.personality) {
        const profileData = {
          personality: result.personality, competencies: result.competencies,
          career: result.career, motivation: result.motivation,
          evidence_data: result.evidence,
        }
        await saveProfile(MY_USER_ID, profileData)
        const raw = {
          personality: result.personality, competencies: result.competencies,
          career: result.career, motivation: result.motivation,
          personality_data: result.personality, competencies_data: result.competencies,
          career_data: result.career, motivation_data: result.motivation,
          _evidence: result.evidence || {}, evidence_data: result.evidence || {},
        }
        setProfile(raw)
        setSurveyStatus({ group1_done: true, group2_done: true })
        setSurveyStep('done')
        getSurveyResponses(MY_USER_ID).then(d => setSurveyResponses(d.responses || [])).catch(() => {})
      }
    } catch (e) {
      setError('Ошибка анализа: ' + e.message)
      setSurveyStep('choice')
    }
  }

  async function startExtraSession() {
    if (surveySessionsCompleted >= 5) return
    setSurveySessionLoading(true)
    setError(null)
    try {
      const nextSession = surveySessionsCompleted + 1
      const result = await generateSurveySession(MY_USER_ID, nextSession)
      if (result.error) throw new Error(result.error)
      setSurveyQuestions(result.questions || [])
      setSurveyGroupNum(2)
      setSurveyStep('group2_extra')  // special step for extra sessions
      // Store session number for submit
      setSurveyGroupNum(200 + nextSession) // encode: 200+session for submit handler
    } catch (e) {
      setError('Ошибка генерации вопросов: ' + e.message)
    } finally {
      setSurveySessionLoading(false)
    }
  }

  const resumeCompany = resumeData?.vacancy?.company || (resumeData ? '...' : null)
  const ops = ownerPublicSettings || DEFAULT_OWNER_PUBLIC_SETTINGS
  const navItems = [
    ...(_resumeSlugMatch ? [{ id: 'resume_apply', label: resumeCompany ? `Отклик · ${resumeCompany}` : 'Отклик…' }] : []),
    { id: 'analysis',      label: 'Анализ личности',   guestHidden: !ops.show_personality },
    { id: 'mindmap',       label: 'Mindmap',            guestHidden: !ops.show_mindmap },
    { id: 'career',        label: 'Карьера',            guestHidden: !ops.show_career },
    { id: 'natal',         label: 'Натальная карта',   guestHidden: !ops.show_natal },
    { id: 'bot',           label: 'Цифровой двойник',  guestHidden: !ops.show_chat },
    { id: 'development',   label: 'Развитие' },
    { id: 'mentor',        label: 'Наставник' },
    { id: 'facts',         label: factsCount !== null ? `Факты · ${factsCount}` : 'Факты', guestHidden: !ops.show_facts },
    { id: 'music',         label: 'Музыка',            guestHidden: !ops.show_music },
    { id: 'threads',       label: 'Threads', ownerOnly: true },
    { id: 'participants',  label: 'Свои', ownerOnly: true },
  ].filter(n => (!n.ownerOnly || isOwner) && (!n.guestHidden || isOwner))

  // If current view is not in navItems (hidden from guest) — redirect to first available
  useEffect(() => {
    if (!navItems.find(n => n.id === view)) {
      const first = navItems[0]
      if (first) setView(first.id)
    }
  }, [navItems.map(n => n.id).join(','), view])

  const subTabs = [
    { id: 'personality',   label: 'Личность',     ownerOnly: false },
    { id: 'competencies',  label: 'Компетенции',  ownerOnly: false },
    { id: 'career',        label: 'Карьера',       ownerOnly: false },
    { id: 'motivation',    label: 'Мотивация',     ownerOnly: false },
    { id: 'socionics',     label: 'Соционика',     ownerOnly: false },
    { id: 'physiognomy',   label: 'Физиогномика',  ownerOnly: false },
    { id: 'style',         label: 'Стиль речи',    ownerOnly: false },
    { id: 'survey',        label: `Анкета${surveyStatus?.group2_done ? ' ✓' : ''}`, ownerOnly: false },
    { id: 'raw',           label: 'JSON',          ownerOnly: true },
  ].filter(t => !t.ownerOnly || isOwner)

  // ── /invite/:token — registration page ───────────────────────────────────
  if (_inviteMatch) {
    return (
      <ErrorBoundary>
        <RegisterPage
          token={_inviteMatch[1]}
          onRegistered={(data) => {
            // After registration: participant logs in as usual
            if (data.role === 'owner' || data.is_owner) {
              // Full reload re-evaluates MY_USER_ID from the localStorage
              // dt_owner_participant_id that setAuth() just wrote (module-level const).
              window.location.href = '/'
            } else {
              setParticipantSession({ participant_id: data.participant_id, display_name: data.display_name })
              window.history.replaceState({}, '', '/')
            }
          }}
        />
      </ErrorBoundary>
    )
  }

  // ── /u/{id} — публичная страница участника ───────────────────────────────
  if (_participantPublicMatch) {
    const pubId = parseInt(_participantPublicMatch[1])
    const storedParticipantId = localStorage.getItem('dt_participant_id') || localStorage.getItem('dt_owner_participant_id')
    const isOwnPage = String(storedParticipantId) === String(pubId)
    const isOwnerVisiting = isOwner
    const guestMode = !isOwnPage && !isOwnerVisiting
    return (
      <ErrorBoundary>
        <ParticipantView
          participantSession={{ participant_id: pubId, display_name: '' }}
          isGuest={guestMode}
          onLogout={guestMode ? null : () => {
            authLogout().then(() => {
              setIsOwner(false)
              window.location.href = '/'
            })
          }}
        />
      </ErrorBoundary>
    )
  }

  // Если вошёл как участник — показываем его личный кабинет
  if (participantSession) {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Space+Mono:wght@400;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <style>{`*,*::before,*::after{box-sizing:border-box}html,body{margin:0;padding:0;height:100%;background:#05050d;overflow:hidden}#root{height:100vh;display:flex;flex-direction:column}::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:rgba(180,185,230,0.15);border-radius:2px}`}</style>
        <ParticipantView
          participantSession={participantSession}
          onLogout={() => {
            authLogout().then(() => {
              setIsOwner(false)
              setParticipantSession(null)
            })
          }}
        />
      </>
    )
  }

  // ── Impersonation mode — owner views as participant ───────────────────────
  if (impersonating) {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Space+Mono:wght@400;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <style>{`*,*::before,*::after{box-sizing:border-box}html,body{margin:0;padding:0;height:100%;background:#05050d;overflow:hidden}#root{height:100vh;display:flex;flex-direction:column}::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:rgba(180,185,230,0.15);border-radius:2px}`}</style>
        <ParticipantView
          participantSession={{ participant_id: impersonating.id, display_name: impersonating.displayName }}
          isOwnerImpersonating={true}
          onStopImpersonating={() => setImpersonating(null)}
          onLogout={() => setImpersonating(null)}
        />
      </>
    )
  }

  return (
    <>
      {/* Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Space+Mono:wght@400;700&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          onLogin={() => { window.location.reload() }}
          onParticipantLogin={(data) => {
            setParticipantSession(data)
            setShowLogin(false)
          }}
          onClose={() => setShowLogin(false)}
        />
      )}

      {/* Статичный звёздный фон */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'url(/stars.svg)', backgroundSize: 'cover', backgroundPosition: 'center', pointerEvents: 'none' }} />

      {/* Глобальные стили */}
      <style>{`
        *,*::before,*::after{box-sizing:border-box}
        html,body{margin:0;padding:0;height:100%;background:#05050d;overflow:hidden}
        #root{height:100vh;display:flex;flex-direction:column}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:rgba(180,185,230,0.15);border-radius:2px}
        .no-scrollbar::-webkit-scrollbar{display:none}
        .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
        @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes pulseGold{0%,100%{box-shadow:0 0 0 0 rgba(224,184,74,0.5)}50%{box-shadow:0 0 14px 4px rgba(224,184,74,0.55)}}
        .pulse-gold{animation:pulseGold 1.8s ease-in-out infinite;border-color:rgba(224,184,74,0.65)!important;color:#e0b84a!important}
        .fade-in{animation:fadeUp .4s ease forwards}
      `}</style>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100vh', color: C.text, fontFamily: font.body }}>

        {/* ── Топ-навигация ── */}
        <nav style={{
          flexShrink: 0,
          borderBottom: `1px solid ${C.border}`,
          background: 'rgba(5,5,13,0.75)', backdropFilter: 'blur(20px)',
        }}>
          {/* Верхняя строка: лого + статус + кнопка */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 36px',
            height: 48,
          }}>
            {/* Лого */}
            <div style={{ fontFamily: font.heading, fontSize: isMobile ? 14 : 17, fontWeight: 300, letterSpacing: '0.15em', color: C.silverHi, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: `1px solid ${C.borderHi}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: C.silver, flexShrink: 0 }}>∴</div>
              {!isMobile && 'Digital Twin'}
            </div>

            {/* На десктопе — табы по центру */}
            {!isMobile && (
              <div style={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {navItems.map(n => (
                  <button key={n.id} onClick={() => setView(n.id)} style={{
                    padding: '5px 16px', borderRadius: 4, cursor: 'pointer',
                    fontFamily: font.body, fontSize: 11.5, fontWeight: 400, letterSpacing: '0.05em',
                    background: view === n.id ? C.silverGlow : 'none',
                    border: `1px solid ${view === n.id ? C.borderHi : 'transparent'}`,
                    color: view === n.id ? C.silverHi : C.silverLo,
                    transition: 'all 0.2s',
                  }}>
                    {n.label}
                  </button>
                ))}
              </div>
            )}

            {/* Правая часть */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, border: `1px solid ${apiStatus === 'online' ? 'rgba(77,200,138,0.3)' : 'rgba(212,90,90,0.3)'}`, fontFamily: font.mono, fontSize: 9, color: apiStatus === 'online' ? C.green : C.red }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: apiStatus === 'online' ? C.green : C.red, animation: apiStatus === 'online' ? 'pd 2s ease-in-out infinite' : 'none', flexShrink: 0 }} />
                {isMobile ? (apiStatus === 'online' ? '●' : '✗') : `API ${apiStatus}`}
              </div>
              <button
                onClick={() => {
                  if (isOwner) {
                    authLogout().then(() => {
                      setIsOwner(false)
                      setImpersonating(null)
                    })
                  } else {
                    setShowLogin(true)
                  }
                }}
                style={{ padding: '4px 10px', borderRadius: 4, border: `1px solid ${C.border}`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}
              >
                {isOwner ? '🔓' : '🔒'}{!isMobile && (isOwner ? ' Выйти' : ' Войти')}
              </button>
            </div>
          </div>

          {/* На мобиле — табы в отдельной строке с горизонтальным скроллом */}
          {isMobile && (
            <div style={{
              display: 'flex', overflowX: 'auto', overflowY: 'hidden', padding: '0 16px',
              borderTop: `1px solid ${C.border}`,
              scrollbarWidth: 'none',
            }}>
              {navItems.map(n => (
                <button key={n.id} onClick={() => setView(n.id)} style={{
                  flexShrink: 0, padding: '8px 14px', cursor: 'pointer',
                  fontFamily: font.body, fontSize: 11, fontWeight: 400,
                  background: 'none', border: 'none',
                  borderBottom: `2px solid ${view === n.id ? C.silverHi : 'transparent'}`,
                  color: view === n.id ? C.silverHi : C.silverLo,
                  whiteSpace: 'nowrap', transition: 'all 0.2s',
                }}>
                  {n.label}
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* ── Под-навигация (только для Анализ) ── */}
        {view === 'analysis' && profile && (
          <div style={{
            display: 'flex', alignItems: 'center', flexShrink: 0,
            padding: isMobile ? '0 0 0 4px' : '0 36px',
            height: 40, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none',
            borderBottom: `1px solid ${C.border}`,
            background: 'rgba(5,5,13,0.5)', backdropFilter: 'blur(10px)',
          }}>
            {subTabs.map(t => (
              <div
                key={t.id}
                onClick={() => setAnalysisTab(t.id)}
                style={{
                  flexShrink: 0,
                  padding: isMobile ? '0 12px' : '0 16px',
                  height: 40, display: 'flex', alignItems: 'center',
                  fontFamily: font.body, fontSize: isMobile ? 11 : 11.5,
                  letterSpacing: '0.04em',
                  color: analysisTab === t.id ? C.silverHi : C.silverLo,
                  borderBottom: `1.5px solid ${analysisTab === t.id ? C.silverHi : 'transparent'}`,
                  cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </div>
            ))}
            {isOwner && !isMobile && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => !recalcLoading && runRecalculate()}
                  disabled={recalcLoading || !profile}
                  title="Пересчитать личность и соционику на основе проверенных фактов и голоса"
                  style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid rgba(224,152,88,0.5)`, background: recalcLoading ? 'rgba(224,152,88,0.12)' : 'none', color: recalcLoading ? C.orange : C.silverLo, fontFamily: font.body, fontSize: 11, cursor: recalcLoading ? 'wait' : 'pointer', opacity: recalcLoading || !profile ? 0.6 : 1 }}
                >
                  {recalcLoading
                    ? (recalcStages
                        ? recalcStages.map(s => `${s.label}${s.status === 'done' ? ' ✓' : s.status === 'running' ? '…' : s.status === 'retrying' ? ' ↺' : s.status === 'error' ? ' ✗' : ''}`).join(' → ')
                        : '⏳ Пересчёт...')
                    : '⟳ Пересчитать'}
                </button>
                {!recalcLoading && <ModelToggle prefKey="personality" />}
                <button
                  onClick={() => !deepLoading && runDeepAnalysis()}
                  disabled={deepLoading}
                  title="Полный анализ всех накопленных данных методом map-reduce"
                  style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.accent}60`, background: deepLoading ? `${C.accent}15` : 'none', color: deepLoading ? C.accent : C.silverLo, fontFamily: font.body, fontSize: 11, cursor: deepLoading ? 'wait' : 'pointer', opacity: deepLoading ? 0.7 : 1 }}
                >
                  {deepLoading ? '⏳ Глубокий анализ...' : '◎ Глубокий анализ'}
                </button>
                {extractionRunning && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 4, border: `1px solid ${C.accent}40`, background: `${C.accent}10`, color: C.accent, fontFamily: font.body, fontSize: 11 }}>
                    <span style={{ animation: 'spin 1.2s linear infinite', display: 'inline-block', fontSize: 13 }}>⟳</span>
                    извлекаю факты
                  </span>
                )}
                {clearConfirm ? (
                  <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'rgba(220,90,90,0.8)', fontFamily: font.body }}>Точно удалить?</span>
                    <button
                      onClick={async () => {
                        setClearConfirm(false)
                        const r = await clearChunks(MY_USER_ID)
                        if (r.error) { setMsg(`Ошибка: ${r.error}`); return }
                        setProfile(null)
                        setShowUpload(true)
                        setMsg(`Профиль удалён (${r.deleted} чанков)`)
                      }}
                      style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid rgba(220,90,90,0.6)', background: 'rgba(220,90,90,0.15)', color: 'rgba(220,90,90,0.9)', fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}
                    >да</button>
                    <button
                      onClick={() => setClearConfirm(false)}
                      style={{ padding: '4px 10px', borderRadius: 4, border: `1px solid ${C.border}`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}
                    >нет</button>
                  </span>
                ) : (
                  <button
                    onClick={() => setClearConfirm(true)}
                    title="Удалить весь профиль и базу знаний"
                    style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.border}`, background: 'none', color: 'rgba(220,90,90,0.6)', fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}
                  >
                    ✕ Очистить данные
                  </button>
                )}
                {surveyStatus?.group2_done ? (
                  <>
                    <button onClick={startSurvey} style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.green}40`, background: 'none', color: C.green, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}>
                      ✓ анкета · повторить
                    </button>
                    <button onClick={async () => { if (!confirm('Удалить все данные анкеты?')) return; await clearSurvey(MY_USER_ID); setSurveyStatus({ group1_done: false, group2_done: false }); setSurveyStep(null); setSurveyQuestions([]); setSurveyGroupNum(1); setSurveyResponses([]) }} style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.red}40`, background: 'none', color: `${C.red}99`, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}>
                      ✕ анкета
                    </button>
                  </>
                ) : (
                  <button onClick={startSurvey} disabled={surveyLoading} style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.accent}40`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}>
                    {surveyLoading ? '◎ Загрузка...' : '◈ Пройти анкету'}
                  </button>
                )}
                <button
                  onClick={() => setShowUpload(v => !v)}
                  style={{ padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.border}`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer' }}
                >
                  {showUpload ? '▲ Скрыть' : '↑ Обновить данные'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Контент ── */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: view === 'mindmap' ? 'hidden' : 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* Mindmap — full height, no padding wrapper */}
          {view === 'mindmap' && (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ErrorBoundary><Mindmap profile={profile} /></ErrorBoundary>
            </div>
          )}

          {view !== 'mindmap' && (
          <div style={{ padding: isMobile ? '16px 14px 60px' : '28px 36px 60px' }}>

            {/* Сообщения */}
            {error && <div style={{ marginBottom: 16, padding: '10px 16px', background: 'rgba(212,90,90,0.1)', border: `1px solid ${C.red}40`, borderRadius: 8, color: C.red, fontFamily: font.body, fontSize: 13 }}>{error}</div>}
            {msg   && <div style={{ marginBottom: 16, padding: '10px 16px', background: 'rgba(77,200,138,0.1)', border: `1px solid ${C.green}40`, borderRadius: 8, color: C.green, fontFamily: font.body, fontSize: 13 }}>{msg}</div>}

            {/* Отклик · [Компания] — публичная ссылка на резюме */}
            {view === 'resume_apply' && (
              <ErrorBoundary>
                {resumeData ? (
                  <ApplicationPackage
                    vacancy={resumeData.vacancy}
                    userId={resumeData.user_id}
                    initialData={resumeData.content}
                    initialSlug={_resumeSlugMatch?.[1]}
                    isReadOnly={!isOwner}
                    onClose={() => setView('analysis')}
                  />
                ) : (
                  <div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: font.mono, fontSize: 11, color: C.silverLo, letterSpacing: '0.1em' }}>
                    загрузка...
                  </div>
                )}
              </ErrorBoundary>
            )}

            {/* Цифровой двойник */}
            {view === 'bot' && (
              <ErrorBoundary>
                <div>
                  <VisitorChat />

                  {isOwner && factsCount !== null && (
                    <div style={{ marginTop: 16 }}>
                      <button
                        onClick={() => setView('facts')}
                        style={{ padding: '6px 16px', borderRadius: 6, border: `1px solid ${C.accent}50`, background: 'none', color: C.silverLo, fontFamily: font.mono, fontSize: 12, cursor: 'pointer' }}
                      >
                        ◈ факты · {factsCount} →
                      </button>
                    </div>
                  )}

                  {isOwner && (
                    <div style={{ marginTop: 24, padding: '16px 20px', background: 'rgba(255,255,255,0.025)', borderRadius: 10, border: `1px solid ${C.border}` }}>
                      <div style={{ fontFamily: font.heading, fontSize: 14, color: C.silverHi, marginBottom: 12 }}>
                        Публичная страница
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                        <code style={{ flex: 1, fontFamily: font.mono, fontSize: 11, color: C.silverLo, background: 'rgba(255,255,255,0.04)', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {window.location.origin}
                        </code>
                        <button
                          onClick={() => { navigator.clipboard?.writeText(window.location.origin).catch(() => {}) }}
                          style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.borderHi}`, background: 'transparent', color: C.accent, fontFamily: font.mono, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          ⎘ копировать
                        </button>
                      </div>
                      <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, marginBottom: 8 }}>
                        что видно гостям:
                      </div>
                      <VisibilitySettings
                        settings={ownerPublicSettings || DEFAULT_OWNER_PUBLIC_SETTINGS}
                        saving={ownerVisibilitySaving}
                        onChange={async (next) => {
                          setOwnerPublicSettings(next)
                          setOwnerVisibilitySaving(true)
                          try { await saveOwnerPublicSettings(MY_USER_ID, next) }
                          catch (e) { console.error(e) }
                          finally { setOwnerVisibilitySaving(false) }
                        }}
                      />
                    </div>
                  )}

                  {isOwner && (
                    <>
                      <div style={{ fontFamily: font.heading, fontSize: 22, fontWeight: 300, color: C.silverHi, margin: '32px 0 16px', paddingTop: 24, borderTop: `1px solid ${C.border}` }}>
                        Логи диалогов
                      </div>
                      <BotLogs />
                    </>
                  )}
                </div>
              </ErrorBoundary>
            )}

            {/* Career Navigator */}
            {view === 'career' && <ErrorBoundary><CareerNavigator userId={MY_USER_ID} isOwner={isOwner} profileExists={!!profile} rolesState={careerRolesState} onRolesChange={setCareerRolesState} /></ErrorBoundary>}

            {/* Натальная карта */}
            {view === 'natal' && <ErrorBoundary><NatalChart userId={MY_USER_ID} isOwner={isOwner} /></ErrorBoundary>}

            {view === 'music' && (isOwner || ops.show_music) && (
              <ErrorBoundary>
                <div style={{ padding: isMobile ? '16px 12px 60px' : '24px 28px 60px' }}>
                  <MusicTab userId={MY_USER_ID} isOwner={isOwner} ownerUserId="user" />
                </div>
              </ErrorBoundary>
            )}

            {/* Развитие */}
            {view === 'development' && <ErrorBoundary><Development userId={MY_USER_ID} isOwner={isOwner} isMobile={isMobile} /></ErrorBoundary>}

            {view === 'mentor' && <ErrorBoundary><Mentor userId={MY_USER_ID} isOwner={isOwner} /></ErrorBoundary>}

            {/* Факты */}
            {view === 'facts' && (isOwner || ops.show_facts) && (
              <ErrorBoundary>
                <div style={{ padding: isMobile ? '16px 12px 60px' : '24px 28px 60px' }}>
                  <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? 22 : 28, fontWeight: 600, color: C.silverHi, margin: 0 }}>
                      Факты
                    </h2>
                    {extractionRunning && (
                      <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.accent, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                        извлекаю факты...
                      </span>
                    )}
                    {isOwner && (
                      <button
                        onClick={() => runExtractFacts(MY_USER_ID)}
                        disabled={factsLoading || extractionRunning}
                        className={factsCount === null && dataStats && dataStats.total > 0 ? 'pulse-gold' : ''}
                        style={{ marginLeft: 'auto', padding: '4px 13px', borderRadius: 4, border: `1px solid ${C.accent}60`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 11, cursor: 'pointer', opacity: factsLoading ? 0.5 : 1 }}
                      >
                        {factsLoading ? '⏳ извлекаю...' : '↻ переизвлечь все'}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
                    {[{ id: 'facts', label: 'Факты' }, { id: 'voice', label: 'Голос и манера' }].map(t => (
                      <button
                        key={t.id}
                        onClick={() => setFactsSubTab(t.id)}
                        style={{
                          padding: '8px 14px', background: 'none', border: 'none',
                          borderBottom: `2px solid ${factsSubTab === t.id ? C.accent : 'transparent'}`,
                          color: factsSubTab === t.id ? C.silverHi : C.silverLo,
                          fontFamily: font.body, fontSize: 12.5, cursor: 'pointer', marginBottom: -1,
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  {factsSubTab === 'facts' ? (
                    <FactsViewer
                      userId={MY_USER_ID}
                      onCountChange={setFactsCount}
                      extractionJobId={extractionJobId}
                      isOwner={isOwner}
                    />
                  ) : (
                    <VoiceNotesViewer userId={MY_USER_ID} />
                  )}
                </div>
              </ErrorBoundary>
            )}

            {/* Threads редактор */}
            {view === 'threads' && <ErrorBoundary><ThreadsEditor userId={MY_USER_ID} /></ErrorBoundary>}

            {/* Свои — только для владельцев */}
            {view === 'participants' && isOwner && (
              <ErrorBoundary>
                <ParticipantsTab telegramData={lastTelegramData} threadsData={lastThreadsData} isOwner={isOwner} />
              </ErrorBoundary>
            )}

            {/* Анализ */}
            {view === 'analysis' && (
              <>
                {loading && step && <ProgressBar step={step} />}

                {profileLoading && (
                  <div style={{ textAlign: 'center', padding: '80px 0', color: C.silverLo, fontFamily: font.body }}>
                    Загрузка профиля...
                  </div>
                )}

                {/* Survey flows — независимо от наличия профиля */}
                {!profileLoading && !loading && (surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'group2_extra') && (
                  <Survey
                    questions={surveyQuestions}
                    groupNum={surveyGroupNum}
                    totalGroups={2}
                    onSubmit={handleSurveySubmit}
                    loading={surveyLoading}
                  />
                )}
                {surveyStep === 'analyzing' && (
                  <SurveyAnalyzingScreen />
                )}
                {surveyStep === 'done' && profile && (
                  <SurveyDoneScreen
                    personality={profile.personality_data}
                    onViewProfile={() => setSurveyStep(null)}
                  />
                )}

                {/* Нет профиля, не в анкете, данных ещё нет (или явно попросили загрузить/обновить) */}
                {!profileLoading && !profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && (showUpload || !(dataStats && dataStats.total > 0)) && (
                  surveyStep === 'choice'
                    ? <SurveyChoice
                        hasSurvey={surveyStatus?.group1_done}
                        hasData={false}
                        onChooseSurvey={() => { setSurveyStep(null); startSurvey() }}
                        onChooseUpload={() => { setSurveyStep(null); setShowUpload(true) }}
                      />
                    : <FileUpload onReady={runIngest} onTelegramChange={setLastTelegramData} onThreadsChange={setLastThreadsData} />
                )}

                {/* FileUpload поверх профиля (кнопка "Обновить данные") */}
                {isOwner && showUpload && profile && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && (
                  <div style={{ marginBottom: 20 }}>
                    <FileUpload onReady={runIngest} onTelegramChange={setLastTelegramData} onThreadsChange={setLastThreadsData} />
                  </div>
                )}

                {/* Данные загружены, анализ ещё не запускался — пустой профиль + явный триггер */}
                {!profileLoading && !profile && !loading && isOwner && !showUpload && dataStats && dataStats.total > 0 && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && (
                  <GlassPanel style={{ padding: '32px 28px', textAlign: 'center', marginBottom: 20 }}>
                    <div style={{ fontFamily: font.heading, fontSize: 22, color: C.silverHi, marginBottom: 8 }}>
                      Данные загружены
                    </div>
                    <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, marginBottom: 22 }}>
                      В базе {dataStats.total} чанков. Анализ ещё не запускался.
                    </div>
                    {pendingStyleData ? (
                      <button
                        onClick={runAnalyzeNow}
                        disabled={loading}
                        style={{ padding: '10px 28px', borderRadius: 8, border: `1px solid ${C.accent}`, background: C.accentDim, color: C.silverHi, fontFamily: font.body, fontSize: 14, cursor: 'pointer' }}
                      >
                        ▶ Начать анализ
                      </button>
                    ) : (
                      <>
                        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
                          Чтобы запустить анализ сейчас, загрузите файл ещё раз.
                        </div>
                        <button
                          onClick={() => { setSurveyStep(null); setShowUpload(true) }}
                          style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.silverLo, fontFamily: font.body, fontSize: 13, cursor: 'pointer' }}
                        >
                          ↑ Загрузить данные
                        </button>
                      </>
                    )}
                  </GlassPanel>
                )}

                {dataStats && dataStats.total > 0 && !loading && (
                  <DataStatsBar stats={dataStats} />
                )}

                {!profileLoading && profile && !loading && profile._deepAnalysis && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '7px 14px', borderRadius: 6, background: 'rgba(100,160,255,0.08)', border: '1px solid rgba(100,160,255,0.2)', fontFamily: font.body, fontSize: 12, color: 'rgba(140,190,255,0.85)' }}>
                    ◎ Глубокий анализ
                    {profile._deepChunks && <span style={{ opacity: 0.6 }}>· {profile._deepChunks} чанков</span>}
                  </div>
                )}

                {!profileLoading && profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'group2_extra' || surveyStep === 'analyzing' || surveyStep === 'done') && analysisTab === 'survey' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <SurveyEvidencePanel responses={surveyResponses} />
                    {/* Блок дополнительных сессий */}
                    {surveyStatus?.group2_done && (
                      <GlassPanel style={{ padding: '20px 24px' }}>
                        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, fontWeight: 600, color: C.silverHi, marginBottom: 8 }}>
                          Дополнительные сессии
                        </div>
                        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: C.silverLo, margin: '0 0 16px', lineHeight: 1.6 }}>
                          Для большей точности вы можете ответить на дополнительные блоки вопросов. Каждая сессия генерируется на основе всех предыдущих ответов и исследует неопределённые области.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {[1,2,3,4,5].map(n => (
                              <div key={n} style={{
                                width: 28, height: 28, borderRadius: 4,
                                border: `1px solid ${n <= surveySessionsCompleted ? C.accent : C.border}`,
                                background: n <= surveySessionsCompleted ? `${C.accent}20` : 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: 'Space Mono, monospace', fontSize: 11,
                                color: n <= surveySessionsCompleted ? C.accent : C.muted,
                              }}>{n <= surveySessionsCompleted ? '✓' : n}</div>
                            ))}
                          </div>
                          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: C.silverLo }}>
                            {surveySessionsCompleted}/5 пройдено
                          </span>
                          {surveySessionsCompleted < 5 ? (
                            <button
                              onClick={startExtraSession}
                              disabled={surveySessionLoading}
                              style={{ padding: '6px 16px', borderRadius: 6, border: `1px solid ${C.accent}60`, background: surveySessionLoading ? `${C.accent}15` : 'none', color: surveySessionLoading ? C.accent : C.silverHi, fontFamily: font.body, fontSize: 12, cursor: surveySessionLoading ? 'wait' : 'pointer' }}
                            >
                              {surveySessionLoading ? '◎ Генерирую вопросы...' : `Пройти сессию ${surveySessionsCompleted + 1}`}
                            </button>
                          ) : (
                            <span style={{ fontFamily: font.body, fontSize: 12, color: C.accent }}>✓ Все сессии пройдены</span>
                          )}
                          {showSurveyAnalyzeBtn && (
                            <button
                              onClick={async () => { setShowSurveyAnalyzeBtn(false); setSurveyStep('analyzing'); await runSurveyAnalysis() }}
                              style={{ padding: '6px 16px', borderRadius: 6, border: `1px solid rgba(80,200,120,0.5)`, background: 'rgba(80,200,120,0.1)', color: 'rgba(140,220,160,0.9)', fontFamily: font.body, fontSize: 12, cursor: 'pointer' }}
                            >
                              ↻ Обновить анализ
                            </button>
                          )}
                        </div>
                      </GlassPanel>
                    )}
                  </div>
                )}
                {!profileLoading && profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && analysisTab === 'socionics' && (
                  <SocionicsTab userId={MY_USER_ID} isOwner={isOwner} />
                )}
                {!profileLoading && profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && analysisTab === 'physiognomy' && (
                  <PhysiognomyTab userId={MY_USER_ID} isOwner={isOwner} />
                )}
                {!profileLoading && profile && !loading && !(surveyStep === 'group1' || surveyStep === 'group2' || surveyStep === 'analyzing' || surveyStep === 'done') && analysisTab !== 'socionics' && analysisTab !== 'survey' && analysisTab !== 'physiognomy' && (
                  <ProfileResult
                    profile={profile}
                    isOwner={isOwner}
                    userId={MY_USER_ID}
                    activeTab={analysisTab}
                    onTabChange={setAnalysisTab}
                    isMobile={isMobile}
                    learningBonuses={learningBonuses}
                  />
                )}

                {/* Просматривать как + Пригласить — только в JSON-табе */}
                {isOwner && analysisTab === 'raw' && (
                  <OwnerControlsPanel
                    onImpersonate={handleImpersonate}
                    participants={participantsList}
                  />
                )}

              </>
            )}
          </div>
          )} {/* end view !== 'mindmap' */}
        </div>
      </div>
      <style>{`@keyframes pd{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
    </>
  )
}
